import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';

export interface UseByoCodeConfiguredOptions {
  item: any;
  isByoShell?: boolean;
  isPreview?: boolean;
  /**
   * BYO create-flow canvas: skip the backfill check entirely. A source fresh
   * out of Step 1 cannot have code yet, so there is nothing to optimistically
   * assume or verify — assuming "configured" here is what caused the Step 2
   * card to render as a real Code Processor for a beat, then flicker back to
   * Blocked once the check resolved. Create mode always renders the real
   * signal (`codeConfigured`), no in-between state.
   */
  isCreateFlow?: boolean;
  /**
   * False skips the backfill fetch (and never reports `checking`) — for
   * callers that render for every flow type (LastRunNode) and only need the
   * signal for BYO flows.
   */
  enabled?: boolean;
}

export interface UseByoCodeConfiguredResult {
  /** True once a code container is actually attached to the source. */
  codeConfigured: boolean;
  /** True while the backfill check is in flight (never true in create mode). */
  checking: boolean;
  /** The resolved code container id, if any (item, then the sources store). */
  codeContainerId: number | string | null;
}

// One in-flight source-detail request per source id, shared by every node
// of that source (CodeNode, ByoDestShellNode, LastRunNode all mount together).
const inflightBackfills = new Map<string, Promise<number | string | null>>();
function fetchSourceOnce(
  sourceId: any,
  getSourceById: (id: any) => Promise<any>,
): Promise<number | string | null> {
  const key = String(sourceId);
  const existing = inflightBackfills.get(key);
  if (existing) return existing;
  const request = (async () => {
    const fetched = await getSourceById(sourceId);
    return fetched?.code_container_id ?? null;
  })();
  inflightBackfills.set(key, request);
  // Always async, so it can't run before the set above.
  const clear = () => inflightBackfills.delete(key);
  request.then(clear, clear);
  return request;
}

/**
 * Shared "is the BYO code processor configured" signal — the ONE source of
 * truth for CodeNode (renders the processor itself), ByoDestShellNode (must
 * stay blocked until code is actually written) and LastRunNode (Run Now gate).
 *
 * TODO(byo): backend proactiveness shim — remove once admin-api-python adds
 * code_container_id to the /flows (and /flows/search) data_sources payload.
 *
 * The flows-LIST response never included code_container_id per source — only
 * the single-source detail fetch did. A real, saved source (not a create-flow
 * shell/preview) is eligible to be verified this way. Once that response
 * carries code_container_id directly, `item` will have it on the very first
 * render, `checking` is never true (its own guard short-circuits), and this
 * whole backfill effect becomes dead code that's safe to delete.
 */
export function useByoCodeConfigured({
  item,
  isByoShell = false,
  isPreview = false,
  isCreateFlow = false,
  enabled = true,
}: UseByoCodeConfiguredOptions): UseByoCodeConfiguredResult {
  const sourceId = item?.id;

  const codeContainerId = useSelector((state: any) => {
    if (item?.code_container_id != null) return item.code_container_id;
    const src = state.entities?.sources?.[sourceId];
    return src?.code_container_id ?? null;
  });

  const hasRealSignal =
    codeContainerId != null || !!item?.source_config?.['ui.byo_code_configured'];

  // Flow-listing/detail views only — the create-flow canvas never verifies
  // (see isCreateFlow above), so canVerify is always false there and this
  // effect never fires.
  const canVerify = enabled && !isByoShell && !isPreview && !isCreateFlow && !!sourceId;
  // Keyed by source id: a node re-rendered for a different source must not
  // inherit the previous source's result (nor skip its own check).
  const [backfill, setBackfill] = useState<{
    sourceId: any;
    containerId: number | string | null;
    failed?: boolean;
  } | null>(null);
  const { getSourceById } = useSourcesAdapter();
  useEffect(() => {
    // Keyed on the container id, not hasRealSignal: a ui.byo_code_configured
    // source already counts as configured, but its id (for the chip and
    // label) only comes from the detail fetch.
    if (!canVerify || codeContainerId != null) return;
    // A definitive answer is kept; a failed request is not, so refreshed node
    // data (a new `item`) retries it.
    if (backfill?.sourceId === sourceId && !backfill.failed) return;
    let cancelled = false;
    fetchSourceOnce(sourceId, getSourceById).then(
      (containerId) => {
        if (!cancelled) setBackfill({ sourceId, containerId });
      },
      () => {
        // Resolves to "not configured" rather than leaving the node stuck on
        // "Checking...".
        if (!cancelled) setBackfill({ sourceId, containerId: null, failed: true });
      },
    );
    return () => {
      cancelled = true;
    };
    // getSourceById is a fresh closure every render (useSourcesAdapter isn't
    // memoized) — re-running on it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canVerify, codeContainerId, sourceId, item]);

  const backfillForSource = backfill?.sourceId === sourceId ? backfill : null;
  const backfillContainerId = backfillForSource?.containerId ?? null;

  return {
    codeConfigured: hasRealSignal || backfillContainerId != null,
    checking: canVerify && !hasRealSignal && !backfillForSource,
    codeContainerId: codeContainerId ?? backfillContainerId,
  };
}

export default useByoCodeConfigured;
